import React from "react";
import { Button, Popconfirm, Table, Tag, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import AppLayout from "../components/Layout";
import DataTable from "../components/DataTable";
import usePagedList from "../lib/usePagedList";
import httpClient from "../axios";
import { formatDateTime, notifyError, notifySuccess } from "../helper";

/**
 * Fire-extinguisher module (separate VR app with its own user accounts).
 * One row per app user; the expanded row lists their trainings with a reset action.
 */
const FireExtinguisherStats = () => {
	const http = React.useMemo(() => httpClient(), []);
	const list = usePagedList("user/trainings");
	const [resetting, setResetting] = React.useState(null);

	const reset = (training) => {
		setResetting(training.id);
		http
			.put(`/results-fire-ext/${training.id}/reset`)
			.then(() => {
				notifySuccess("Training reset", `${training.name} can be taken again.`);
				list.reload();
			})
			.catch((error) => notifyError(error, "Could not reset the training"))
			.finally(() => setResetting(null));
	};

	const columns = [
		{
			title: "User",
			key: "user",
			render: (row) => (
				<>
					<div style={{ fontWeight: 600 }}>{[row.first_name, row.last_name].filter(Boolean).join(" ") || row.username || "-"}</div>
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>{row.email}</Typography.Text>
				</>
			),
		},
		{ title: "PIN", dataIndex: "user_pin", key: "user_pin", render: (v) => v || "-" },
		{
			title: "Trainings",
			key: "trainings",
			align: "center",
			render: (row) => (row.trainings || []).length,
		},
		{
			title: "Last completed",
			key: "last",
			render: (row) => {
				const dates = (row.trainings || []).map((t) => t.completion_date).filter(Boolean).sort();
				return dates.length ? formatDateTime(dates[dates.length - 1]) : "-";
			},
		},
	];

	const expanded = (row) => (
		<Table
			size="small"
			pagination={false}
			rowKey="id"
			dataSource={row.trainings || []}
			columns={[
				{ title: "Training", dataIndex: "name", key: "name" },
				{ title: "Attempts", dataIndex: "attempts", key: "attempts", width: 100, align: "center" },
				{ title: "Completed", dataIndex: "completion_date", key: "completion_date", width: 200, render: formatDateTime },
				{
					title: "Outcome",
					dataIndex: "did_user_succeed",
					key: "did_user_succeed",
					width: 110,
					render: (v) => (v ? <Tag color="green">Succeeded</Tag> : <Tag>Not yet</Tag>),
				},
				{
					title: "",
					key: "actions",
					align: "right",
					render: (training) => (
						<Popconfirm
							title="Reset this training so the user can take it again?"
							okText="Reset"
							onConfirm={() => reset(training)}
						>
							<Button size="small" icon={<ReloadOutlined />} loading={resetting === training.id}>
								Reset
							</Button>
						</Popconfirm>
					),
				},
			]}
		/>
	);

	return (
		<AppLayout title="Fire extinguisher training" subtitle="Users of the fire-extinguisher VR app and their completed trainings.">
			<DataTable
				rows={list.rows}
				columns={columns}
				loading={list.loading}
				pagination={list.pagination}
				expandable={{ expandedRowRender: expanded, rowExpandable: (row) => (row.trainings || []).length > 0 }}
			/>
		</AppLayout>
	);
};

export default FireExtinguisherStats;
